import React, { useCallback, useEffect, useRef, useState } from 'react';
import EvidenceLightbox from '../components/EvidenceLightbox';
import {
  getEvidenceUrl,
  listEventEvidenceItems,
  pickPrimaryEvidence,
} from '../../../services/violations';
import { describeEvidenceState, EVIDENCE_STATUS } from '../../../constants/violationConstants';
import { evidenceQueue } from '../../../utils/evidenceQueue';
import { DetectionZonesLegend, DetectionZonesOverlay } from '../../../components/DetectionZonesOverlay/DetectionZonesOverlay';
import useCameraZones from '../../../hooks/useCameraZones';

/**
 * Фотофиксация нарушения (PART 3, раздел 3 + Evidence Addendum v1.0).
 *
 * Панель НЕ читает event.evidence: в реальном GET /violations/events/{id}
 * такого поля нет. Источник данных — отдельный ресурс
 * /events/{id}/evidence, а картинка приходит по временной ссылке
 * /evidence/{evidence_id}/url.
 *
 * evidence_id — стабильная идентичность, url временный. Пока Evidence в
 * processing, список перезапрашивается раз в POLL_MS, пока не станет
 * ready/unavailable; уход со страницы polling останавливает. Если картинка
 * отдала ошибку (401/403/протухшая ссылка), панель ровно один раз запрашивает
 * свежий url; вторая неудача показывает плейсхолдер — бесконечный retry
 * запрещён.
 */
const POLL_MS = 4000;

/**
 * Миниатюра одного из нескольких Evidence. Свою ссылку тянет сама и через
 * общую очередь: у события может быть много кадров, а лимит параллелизма
 * один на всё приложение.
 */
const EvidenceThumb = ({ item, active, onSelect }) => {
  const [url, setUrl] = useState('');

  useEffect(() => {
    if (item.status !== EVIDENCE_STATUS.READY || !item.evidenceId) return undefined;
    let cancelled = false;
    const task = evidenceQueue.enqueue(() => getEvidenceUrl(item.evidenceId));
    task.promise
      .then((link) => { if (!cancelled) setUrl(link?.url ?? ''); })
      // Ошибка и отмена одинаково означают «миниатюры не будет».
      .catch(() => {});
    return () => { cancelled = true; task.cancel(); };
  }, [item.status, item.evidenceId]);

  const state = describeEvidenceState(item);

  return (
    <button
      type="button"
      className={`ved-evidence-thumb${active ? ' ved-evidence-thumb--active' : ''}`}
      onClick={() => onSelect(item.evidenceId)}
      aria-label={item.role === 'primary' ? 'Главное фото' : 'Дополнительное фото'}
      aria-pressed={active}
    >
      {url
        ? <img src={url} alt="" onError={() => setUrl('')} />
        : <span className="ved-evidence-thumb-text">{state.text || '…'}</span>}
    </button>
  );
};

const NO_ZONE_IDS = [];

const EvidencePanel = ({ eventId, caption = '', cameraId = null, zoneIds = NO_ZONE_IDS, snapshotZones = NO_ZONE_IDS }) => {
  const [phase, setPhase] = useState('loading'); // loading | ready | error
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [url, setUrl] = useState('');
  const [urlBroken, setUrlBroken] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const retriedRef = useRef(false);
  // ТЗ V5 №6: текущие зоны камеры события поверх фото; ошибка зон фото не прячет.
  const cameraZones = useCameraZones(cameraId);
  const [zonesVisible, setZonesVisible] = useState(true);

  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  /** silent=true — это тик polling'а: скелетон повторно не показываем. */
  const load = useCallback((silent = false) => {
    if (!eventId) return Promise.resolve();
    if (!silent) setPhase('loading');

    return listEventEvidenceItems(eventId)
      .then((list) => {
        if (cancelledRef.current) return;
        setItems(list);
        setPhase('ready');
      })
      .catch(() => {
        if (cancelledRef.current) return;
        // Сеть/API упали — это ошибка страницы фото, а не «фото недоступно».
        if (!silent) setPhase('error');
      });
  }, [eventId]);

  useEffect(() => {
    setSelectedId('');
    setItems([]);
    load();
  }, [load]);

  const selected = items.find((item) => item.evidenceId === selectedId)
    || pickPrimaryEvidence(items)
    || null;

  // Пока выбранный кадр в processing — перезапрашиваем список.
  useEffect(() => {
    if (selected?.status !== EVIDENCE_STATUS.PROCESSING) return undefined;
    const timer = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(timer);
  }, [selected?.status, load]);

  // Ссылка живёт ровно столько, сколько выбран этот evidence_id.
  const selectedEvidenceId = selected?.evidenceId || '';
  const selectedStatus = selected?.status || '';
  useEffect(() => {
    setUrl('');
    setUrlBroken(false);
    retriedRef.current = false;

    if (selectedStatus !== EVIDENCE_STATUS.READY || !selectedEvidenceId) return undefined;

    let cancelled = false;
    getEvidenceUrl(selectedEvidenceId)
      .then((link) => { if (!cancelled) setUrl(link?.url ?? ''); })
      .catch(() => { if (!cancelled) setUrlBroken(true); });

    return () => { cancelled = true; };
  }, [selectedEvidenceId, selectedStatus]);

  const handleImageError = () => {
    if (retriedRef.current || !selectedEvidenceId) {
      setUrlBroken(true);
      return;
    }
    retriedRef.current = true;
    getEvidenceUrl(selectedEvidenceId)
      .then((link) => {
        const fresh = link?.url ?? '';
        // Та же ссылка браузером не перезапрашивается — считаем её мёртвой.
        if (!fresh || fresh === url) setUrlBroken(true);
        else setUrl(fresh);
      })
      .catch(() => setUrlBroken(true));
  };

  const renderBody = () => {
    if (phase === 'loading') return <div className="ved-evidence-placeholder">Загружаем фото…</div>;

    if (phase === 'error') {
      return (
        <div className="ved-evidence-placeholder">
          Не удалось загрузить фото
          <button type="button" className="vio-btn ved-evidence-retry" onClick={() => load()}>
            Повторить
          </button>
        </div>
      );
    }

    if (items.length === 0) {
      return <div className="ved-evidence-placeholder">Фото отсутствует</div>;
    }

    if (urlBroken) return <div className="ved-evidence-placeholder">Фото недоступно</div>;

    const state = describeEvidenceState(selected || {});
    if (state.kind !== 'ready' || !url) {
      return <div className="ved-evidence-placeholder">{state.text}</div>;
    }

    return (
      <>
        <div className="ved-evidence-frame">
          <DetectionZonesOverlay
            block
            imgClassName="ved-evidence-image"
            src={url}
            alt="Кадр нарушения"
            onImageError={handleImageError}
            zones={cameraZones.zones}
            visible={zonesVisible}
            highlightZoneIds={zoneIds}
          />
          {caption && <span className="ved-evidence-caption">{caption}</span>}
          <button
            type="button"
            className="vio-btn ved-evidence-zoom"
            onClick={() => setLightboxOpen(true)}
          >
            Увеличить фото
          </button>
        </div>
        <DetectionZonesLegend
          status={cameraZones.status}
          zones={cameraZones.zones}
          visible={zonesVisible}
          onToggle={setZonesVisible}
          highlightZoneIds={zoneIds}
          snapshotZones={snapshotZones}
          historical
        />
      </>
    );
  };

  return (
    <div className="vio-card ved-evidence-card">
      <p className="vio-drawer-section-title">Фотофиксация нарушения</p>

      {renderBody()}

      {phase === 'ready' && items.length > 1 && (
        <div className="ved-evidence-thumbs">
          {items.map((item) => (
            <EvidenceThumb
              key={item.evidenceId}
              item={item}
              active={item.evidenceId === selectedEvidenceId}
              onSelect={setSelectedId}
            />
          ))}
        </div>
      )}

      {lightboxOpen && url && (
        <EvidenceLightbox
          url={url}
          caption={caption}
          onClose={() => setLightboxOpen(false)}
          zones={zonesVisible ? cameraZones.zones : []}
          highlightZoneIds={zoneIds}
        />
      )}
    </div>
  );
};

export default EvidencePanel;
